import { useAssessmentQuery, useAssessmentsInstancesQuery, useUsersMap } from "@Components/assessment/queries";
import { ASSESSMENT_STATUSES, IAssessmentStatus, IUseAssessmentData } from "@Components/assessment/types";
import { IAssessmentDetails, IAssessmentInstanceDetails } from "@Models/IAssessmentDetails";
import { IUserDetails } from "@Models/IUserDetails";
import { DateTime, IColumnData, isDevelopment, prettifyName, useSearchParams } from "@Utils";
import { Button } from "antd";
import { DownloadOutlined, FileSearchOutlined, LoadingOutlined } from "@ant-design/icons";
import React, { useState } from "react";
import useAssessmentReport from "./useAssessmentReport";

interface IUseAdminUsersData
    extends IUseAssessmentData<IAssessmentInstanceDetails & { user: IUserDetails["fullName"] }> {
    assessment: IAssessmentDetails;
    assessmentInstances: (IAssessmentInstanceDetails & { user: IUserDetails["fullName"] })[];
    data: {
        status: {
            isLoading: boolean;
            buttons: { title: string; disabled: boolean; key: IAssessmentStatus }[];
        };
    };
    userCount: number;

    onSubmissionTypeChange: (status: (typeof ASSESSMENT_STATUSES)[number]) => void;
}

const useAdminUsers = (): IUseAdminUsersData => {
    // ** State Hooks ** //
    const [assessmentStatus, setAssessmentStatus] = useState<IAssessmentStatus>();

    // ** Search params ** //
    const [searchParams, setSearchParams] = useSearchParams();
    const assessmentParam = searchParams.get("assessment");

    // ** Queries and Mutations ** //
    const {
        data: originalAssessmentInstances,
        isFetched: areAssessmentsInstancesFetched,
        isFetching: areAssessmentsInstancesFetching,
        isLoading: areAssessmentsInstancesLoading,
        refetch: onReload,
    } = useAssessmentsInstancesQuery(assessmentParam);
    const {
        data: assessment,
        isFetched: isAssessmentFetched,
        isFetching: isAssessmentFetching,
        isLoading: isAssessmentLoading,
    } = useAssessmentQuery(assessmentParam);

    const {
        data: usersMap,
        isFetched: areUsersFetched,
        isFetching: areUsersFetching,
        isLoading: areUsersLoading,
    } = useUsersMap();
    const { activeDownloads, prepareReport } = useAssessmentReport();

    // ** Query State Booleans ** //
    const isFetched = isAssessmentFetched && areAssessmentsInstancesFetched && areUsersFetched;
    const isFetching = isAssessmentFetching || areAssessmentsInstancesFetching || areUsersFetching;
    const isLoading = isAssessmentLoading || areAssessmentsInstancesLoading || areUsersLoading;

    // ** Data Transformers ** //
    const uniqueUsers = new Set();
    const assessmentInstances = originalAssessmentInstances
        .map((ai) => {
            uniqueUsers.add(ai.recipient);
            return {
                user: usersMap?.get(ai.recipient)?.fullName,
                ...ai,
            };
        })
        .filter((ai) => (assessmentStatus ? ai.status === assessmentStatus : true));

    const getCorrectDate = (record: IAssessmentInstanceDetails) => {
        switch (assessmentStatus) {
            case "assigned":
                return record.creationDate;
            case "completed":
                return record.completionDate;
            case "submitted":
                return record.submissionDate;
            default:
                return record.creationDate;
        }
    };

    // ** Column Data for Admin Users Table ** //
    const columnData: IColumnData<IAssessmentInstanceDetails & { user: IUserDetails["fullName"] }>[] = [
        {
            key: "user",
            title: "User",
            align: "left",
            sorter: (a, b) => a.user.localeCompare(b.user),
        },
        {
            key: "creationDate",
            title: "Date",
            align: "center",
            render: (_, record) => {
                const date = getCorrectDate(record);
                return DateTime(date).isBlankDate() ? "" : DateTime(date).format("MMM Do YYYY");
            },
        },
        {
            key: "status",
            align: "center",
            sorter: (a, b) => a.status.localeCompare(b.status),
            render: (status: string) => prettifyName(status),
        },
        {
            key: "actions",
            align: "center",
            render: (_, record) => {
                const isDownloading = activeDownloads.has(record.assessmentInstanceId);
                return (
                    record.status !== "assigned" && (
                        <>
                            <Button
                                icon={<FileSearchOutlined />}
                                onClick={() =>
                                    setSearchParams({
                                        tab: "submissions",
                                        assessment: assessmentParam,
                                        assessmentInstance: record.assessmentInstanceId,
                                    })
                                }
                            >
                                Review
                            </Button>
                            {record.status === "completed" && isDevelopment() ? (
                                <Button
                                    disabled={isDownloading}
                                    icon={isDownloading ? <LoadingOutlined /> : <DownloadOutlined />}
                                    onClick={() => prepareReport(record)}
                                >
                                    Download
                                </Button>
                            ) : null}
                        </>
                    )
                );
            },
        },
    ];

    // ** Event Handlers ** //
    const handleSubmissionTypeChange = (status: IAssessmentStatus) => setAssessmentStatus(status);

    const hasAssignedInstances = originalAssessmentInstances.some((ai) => ai.status === "assigned");
    const hasSubmittedInstances = originalAssessmentInstances.some((ai) => ai.status === "submitted");
    const hasCompletedInstances = originalAssessmentInstances.some((ai) => ai.status === "completed");

    // ** Return Data ** //
    return {
        assessment,
        data: {
            status: {
                isLoading,
                buttons: [
                    { title: "Assigned", disabled: !hasAssignedInstances, key: "assigned" },
                    { title: "Submitted", disabled: !hasSubmittedInstances, key: "submitted" },
                    { title: "Completed", disabled: !hasCompletedInstances, key: "completed" },
                ],
            },
        },
        assessmentInstances,
        columnData,
        isFetching,
        isLoading: !isFetched || isLoading,
        onReload,
        onSubmissionTypeChange: handleSubmissionTypeChange,
        userCount: uniqueUsers?.size,
    };
};

export default useAdminUsers;
